import { Popover } from 'antd';
import _ from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { BookingViewTabPopover } from '../other/BookingViews/components/BookingViewTabPopover';
import { Badge, Button, Group, Menu, ScrollArea, Tabs, ThemeIcon } from '@good/ui/core';
import { Bookmark, ChevronDown } from '@good/icons';
import { ICustomView } from 'interfaces/custom-views-interface';

interface BookingViewTabsProps {
  openBookingViewModal: () => void;
  compact?: boolean;
}

export const BookingViewTabs = ({ openBookingViewModal, compact }: BookingViewTabsProps) => {
  const [showTabIconPopover, setShowTabIconPopover] = useState('');
  const dispatch = useDispatch<IRootDispatch>();
  const setHasBookingListingFilterChanged = dispatch.bookingsStore.setHasBookingListingFilterChanged;
  const setBookingListingActiveTab = dispatch.bookingsStore.setBookingListingActiveTab;
  const bookingListingActiveTab = useSelector((state: IRootState) => state.bookingsStore.bookingListingActiveTab);
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const filterCounters = useSelector((state: IRootState) => state.bookingsStore.filterCounters);
  const displayedBookingListingTabs = useSelector(
    (state: IRootState) => state.bookingsStore.displayedBookingListingTabs,
  );
  const setBookingsFilter = dispatch.bookingsStore.setBookingsFilter;
  const doUpdateBookingView = dispatch.bookingsStore.doUpdateBookingView;
  const isBookingListingLoading = useSelector((state: IRootState) => state.bookingsStore.isBookingListingLoading);
  const setSelectedFilterKey = dispatch.bookingsStore.setSelectedFilterKey;

  const changeTab = async (selectedTabId: string) => {
    const activeTab = [...defaultBookingViews, ...displayedBookingListingTabs].find(
      (tab) => tab.customViewId === selectedTabId,
    );
    if (activeTab) {
      setBookingListingActiveTab(activeTab);
      setBookingsFilter(activeTab.filterValue);
      setHasBookingListingFilterChanged(false);
      setSelectedFilterKey(activeTab.customViewId);
    }
  };

  const renderViewTabIcon = (tab: ICustomView) => {
    const togglePopOver = (value) => {
      const showTabIconPopover = value ? tab.customViewId : '';
      setShowTabIconPopover(showTabIconPopover);
    };

    return (
      <Popover
        content={
          <BookingViewTabPopover
            onHidePopOver={togglePopOver}
            currentTab={tab}
            onShowBookingViewModal={openBookingViewModal}
            doUpdateBookingViewTab={doUpdateBookingView}
          />
        }
        visible={showTabIconPopover === tab.customViewId}
        onVisibleChange={togglePopOver}
        placement='bottom'
        trigger='click'
      >
        <ThemeIcon variant='default' size='sm'>
          <ChevronDown />
        </ThemeIcon>
      </Popover>
    );
  };

  if (compact) {
    return (
      <Menu>
        <Menu.Target>
          <Button variant='default' rightSection={<ChevronDown />}>
            {_.get(bookingListingActiveTab, 'name', 'All bookings')}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {defaultBookingViews.map((tab) => {
            const counter = _.find(filterCounters, (counter) => counter.filterName === tab.customViewId);
            return (
              <Menu.Item
                disabled={isBookingListingLoading}
                key={tab.customViewId}
                value={tab.customViewId}
                onClick={() => changeTab(tab.customViewId)}
              >
                <Group wrap='nowrap'>
                  {tab.name}{' '}
                  {counter !== undefined && (
                    <Badge color='gray.1' c='dark'>
                      {counter.count}
                    </Badge>
                  )}
                </Group>
              </Menu.Item>
            );
          })}
          {displayedBookingListingTabs.length > 0 && <Menu.Divider />}
          {displayedBookingListingTabs.map((tab: ICustomView) => {
            const counter = _.find(filterCounters, (counter) => counter.filterName === tab.customViewId);
            const customViewId = tab.customViewId;
            if (!customViewId) {
              return null;
            }
            return (
              <Menu.Item
                disabled={isBookingListingLoading}
                key={tab.customViewId}
                value={tab.customViewId}
                onClick={() => changeTab(tab.customViewId)}
              >
                <Group wrap='nowrap' gap={6}>
                  {tab.isPinned && <Bookmark />}
                  {tab.name}{' '}
                  {counter !== undefined && (
                    <Badge color='gray.1' c='dark'>
                      {counter.count}
                    </Badge>
                  )}
                </Group>
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>
    );
  }

  return (
    <Tabs
      variant='outline'
      mb={-1}
      defaultValue='ALL'
      value={_.get(bookingListingActiveTab, 'customViewId') ?? 'ALL'}
      onChange={changeTab}
      styles={{
        list: {
          flexWrap: 'nowrap',
        },
      }}
    >
      <Group justify='space-between' align='center' wrap='nowrap'>
        <ScrollArea>
          <Tabs.List>
            {defaultBookingViews.map((tab) => {
              const counter = _.find(filterCounters, (counter) => counter.filterName === tab.customViewId);
              return (
                <Tabs.Tab disabled={isBookingListingLoading} key={tab.customViewId} value={tab.customViewId}>
                  <Group wrap='nowrap' gap='xs'>
                    {tab.name}{' '}
                    {counter !== undefined && (
                      <Badge color='gray.1' c='dark'>
                        {counter.count}
                      </Badge>
                    )}
                  </Group>
                </Tabs.Tab>
              );
            })}

            {displayedBookingListingTabs.map((tab: ICustomView) => {
              const counter = _.find(filterCounters, (counter) => counter.filterName === tab.customViewId);
              const customViewId = tab.customViewId;
              if (!customViewId) {
                return null;
              }
              return (
                <Tabs.Tab disabled={isBookingListingLoading} key={tab.customViewId} value={tab.customViewId}>
                  <Group wrap='nowrap' gap={6}>
                    <Group wrap='nowrap' gap={6}>
                      {tab.isPinned && <Bookmark />}
                    </Group>
                    {tab.name}{' '}
                    {counter !== undefined && (
                      <Badge color='gray.1' c='dark'>
                        {counter.count}
                      </Badge>
                    )}
                    {renderViewTabIcon(tab)}
                  </Group>
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </ScrollArea>
      </Group>
    </Tabs>
  );
};
